import type { Country } from "@commercekit/remix";
import { numberValidation, useLocale } from "@commercekit/remix";
import classNames from "classnames";
import type { ChangeEvent, FC, InputHTMLAttributes } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHydrated } from "remix-utils/use-hydrated";
import { useField } from "remix-validated-form";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";

const WvPhoneInputField: FC<
  {
    name: string;
    onChange?: (v: string) => void;
    onDefaultChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    theme?: "normal" | "error" | "valid";
    initialValue?: string | number;
    customError?: string;
    phoneCodeCountries?: Country[];
  } & Omit<InputHTMLAttributes<HTMLInputElement>, "onChange">
> = ({
  name,
  onChange,
  initialValue,
  customError,
  phoneCodeCountries,
  onDefaultChange,
  ...props
}) => {
  const { $pt } = usePrismicLabels();
  const isHydrated = useHydrated();
  const { localeFullName } = useLocale();

  const firstRun = useRef(true);
  const [inputTheme, setInputTheme] = useState<"normal" | "error" | "valid">(
    "normal"
  );

  const sortedCountries = useMemo(
    () =>
      phoneCodeCountries
        ? [...phoneCodeCountries].sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [phoneCodeCountries]
  );

  const [selectedCountry, setSelectedCountry] = useState(
    sortedCountries.find(
      (c) => c.country.toLowerCase() === localeFullName.split("-")[1]
    ) || sortedCountries[0]
  );
  const [phoneNumber, setPhoneNumber] = useState("");
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { error, getInputProps, validate, clearError, touched, setTouched } =
    useField(name);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    let actualValue = value;

    if (selectedCountry.country === "NL" && value.startsWith("06")) {
      actualValue = value.slice(1);
    }

    setPhoneNumber(actualValue);
    setDisplayPhoneNumber(value);

    onChange?.(actualValue);
    onDefaultChange?.(e);

    if (value) {
      clearError();
      setInputTheme("normal");
    }
  };;

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setErrorMessage("");
    setIsDropdownOpen(false);
    sessionStorage.setItem("selectedCountry", country.code);
    if (touched) validate();
  };

  // Validate prefix immediately on input
  const validatePrefix = useCallback((number: string, country: Country) => {
    let cleanNumber = number.replace(/\D/g, "");

    // Allow empty input
    if (!cleanNumber) {
      return true;
    }

    // Special handling for Netherlands - allow both "6..." and "06..."
    if (country.country === "NL" && cleanNumber.startsWith("06")) {
      cleanNumber = cleanNumber.slice(1);
    }

    // Defer prefix validation until the user has typed at least 2 digits,
    // to prevent immediate flash of an error only in the Netherlands
    if (country.country === "NL" && cleanNumber.length < 2) {
      return true;
    }

    return (
      country.prefixes.length === 0 ||
      country.prefixes.some((prefix) => cleanNumber.startsWith(prefix))
    );
  }, []);

  // Get current validation state
  const getCurrentValidation = useCallback(() => {
    if (!phoneNumber) return { isValid: true, message: "" };

    const prefixValid = validatePrefix(phoneNumber.toString(), selectedCountry);

    if (!prefixValid) {
      return {
        isValid: false,
        message: `${$pt(
          "phone_code_first_digit_error"
        )} ${selectedCountry?.prefixes.join(` ${$pt("joiner_or")} `)}`,
      };
    }

    return { isValid: true, message: "" };
  }, [phoneNumber, selectedCountry, validatePrefix, $pt]);

  const validation = useMemo(
    () => getCurrentValidation(),
    [getCurrentValidation]
  );

  useEffect(() => {
    if (!validation.isValid && phoneNumber && validation.message) {
      setErrorMessage(validation.message);
      setInputTheme("error");
      return;
    }

    if (customError || error) {
      setInputTheme("error");
      if (error) {
        const startsWithPhone = error.startsWith("phone_");
        if (
          (startsWithPhone && error.includes("code_length")) ||
          (startsWithPhone && error.includes("code_range"))
        ) {
          const { message, ...values } = numberValidation(
            selectedCountry,
            phoneNumber,
            true
          );
          const stringValues = Object.fromEntries(
            Object.entries(values)
              .filter(([_, v]) => v !== undefined)
              .map(([k, v]) => [k, String(v)])
          );
          setErrorMessage($pt(message, stringValues));
        } else {
          setErrorMessage(error);
        }
      } else {
        setErrorMessage(customError ?? "");
      }
      return;
    }

    // No errors
    setInputTheme("normal");
    setErrorMessage("");
  }, [customError, error, validation, phoneNumber, selectedCountry, $pt]);

  // Initialize phone number and selected country based on initialValue and locale, but only once on first render
  useEffect(() => {
    if (
      !isHydrated ||
      !initialValue ||
      !selectedCountry ||
      !firstRun.current ||
      sortedCountries.length === 0
    ) {
      return;
    }

    let resolvedCountry = selectedCountry;
    let localPhoneNumber = initialValue.toString();

    // Check E.164 format, longest-prefix match, break by current selectedCountry.
    if (localPhoneNumber.startsWith("+")) {
      const matches = sortedCountries.filter((c) =>
        localPhoneNumber.startsWith(c.code)
      );
      const longest = matches.reduce(
        (max, c) => (c.code.length > max ? c.code.length : max),
        0
      );
      const longestMatches = matches.filter((c) => c.code.length === longest);
      const match =
        longestMatches.find((c) => c.country === selectedCountry?.country) ??
        longestMatches[0];

      if (match) {
        resolvedCountry = match;
        localPhoneNumber = localPhoneNumber.slice(match.code.length);
      } else {
        localPhoneNumber = localPhoneNumber.replace(/\D/g, "");
      }
    } else {
      const storedCountryCode = sessionStorage.getItem("selectedCountry");
      if (storedCountryCode) {
        localPhoneNumber = localPhoneNumber.replace(storedCountryCode, "");
        const country = sortedCountries.find(
          (c) => c.code === storedCountryCode
        );
        if (country) resolvedCountry = country;
      }
    }

    if (resolvedCountry.country === "NL" && localPhoneNumber.startsWith("0")) {
      setPhoneNumber(localPhoneNumber.slice(1));
      setDisplayPhoneNumber(localPhoneNumber);
    } else {
      setPhoneNumber(localPhoneNumber);
      setDisplayPhoneNumber(localPhoneNumber);
    }

    if (resolvedCountry !== selectedCountry) {
      setSelectedCountry(resolvedCountry);
    }

    firstRun.current = false;
    sessionStorage.setItem("selectedCountry", resolvedCountry.code);
  }, [isHydrated, initialValue, selectedCountry, sortedCountries]);

  return (
    <div className=" col-12 col-lg-6">
      <div className="d-flex">
        <div className="relative">
          <input
            type="hidden"
            name="phoneCountryCode"
            value={selectedCountry?.code}
            autoComplete="nope"
          />
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              height: "2.5rem",
              padding: "0.5rem 0.75rem",
              border: "1px solid #d1d5db", // Tailwind border-gray-300
              borderRadius: "0.375rem 0 0 0.375rem",
              borderRight: "0",
              background: "#fff",
              cursor: "pointer",
              outline: "none",
              width: "89px",
              transition: "background 0.2s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = "#f9fafb")}
            onMouseOut={(e) => (e.currentTarget.style.background = "#fff")}
            onFocus={(e) =>
              (e.currentTarget.style.boxShadow = "0 0 0 2px #0000")
            }
            onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
          >
            <span style={{ fontSize: "1.125rem" }}>
              <div
                className={classNames(
                  "fflag ff-sm ff-wave",
                  `fflag-${selectedCountry?.country}`
                )}
              ></div>
            </span>
            <span className="d-flex align-items-center">
              <span style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                {selectedCountry?.code}
              </span>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
              >
                <path
                  fill="#000"
                  d="M7.41 8.58L12 13.17l4.59-4.59L18 10l-6 6l-6-6z"
                  strokeWidth="0.5"
                  stroke="none"
                />
              </svg>
            </span>
          </button>

          {isDropdownOpen && (
            <div
              style={{
                position: "absolute",
                left: 0,
                zIndex: 10,
                width: "16rem", // w-64
                overflowY: "auto",
                background: "#fff",
                border: "1px solid #d1d5db", // border-gray-300
                borderRadius: "0.375rem", // rounded-md
                boxShadow:
                  "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)", // shadow-lg
                top: "60%",
                maxHeight: "15rem", // max-h-60
              }}
            >
              {sortedCountries.map((country) => (
                <button
                  key={`${country.country}-${country.code}`}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    gap: "0.75rem", // gap-3
                    padding: "0.5rem 0.75rem", // px-3 py-2
                    textAlign: "left",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    outline: "none",
                    font: "inherit",
                    transition: "background 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.background = "#f3f4f6")
                  } // hover:bg-gray-100
                  onMouseOut={(e) =>
                    (e.currentTarget.style.background = "none")
                  }
                  onFocus={(e) =>
                    (e.currentTarget.style.background = "#f3f4f6")
                  } // focus:bg-gray-100
                  onBlur={(e) => (e.currentTarget.style.background = "none")}
                >
                  <span style={{ fontSize: "1.125rem" }}>
                    <div
                      className={classNames(
                        "fflag ff-sm ff-wave",
                        `fflag-${country.country}`
                      )}
                    ></div>
                  </span>
                  <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                    {country.code}
                  </span>
                  <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                    {country.name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative z-0 flex-1 mb-3">
          <div className="input input--label">
            <input type="hidden" id={name} name={name} value={phoneNumber} />
            <input
              {...getInputProps({ id: `${name}_display`, ...props })}
              type="tel"
              name={`${name}_display`}
              value={displayPhoneNumber}
              placeholder={`${selectedCountry?.prefixes.join("/")}xxxxxxxx`}
              autoComplete="new-password"
              inputMode="numeric"
              data-form-type="other" // To prevent autofill on iOS
              data-lpignore="true" // To prevent LastPass auto-fill
              className={classNames([
                "form-control",
                { valid: inputTheme === "valid" },
                { error: inputTheme === "error" },
                { "": inputTheme === "normal" },
              ])}
              onInput={() => setTouched(true)}
              onChange={handlePhoneChange}
              onBlur={() => validate()}
            />
            <label htmlFor={name} className="input__label">
              {$pt("form_label_phone")} *
            </label>

            {errorMessage || customError ? (
              <span className="error-label" style={{ marginTop: "-3px" }}>
                {$pt(errorMessage ?? customError ?? "")}
              </span>
            ) : null}
          </div>
        </div>
        {isDropdownOpen && (
          <div
            style={{ position: "fixed", inset: 0, zIndex: 0 }}
            onClick={() => setIsDropdownOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default WvPhoneInputField;
